import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import { Plus, Scan, Clock, CheckCircle, AlertCircle, TrendingUp, BarChart3, GitCompareArrows, Eye, ArrowRight } from 'lucide-react';
import axios from 'axios';
import '@/styles/forms.css';
import '@/styles/app-states.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status pills on the token layer. Each carries its WORD as well as its colour
// and its icon, so the state never depends on hue alone.
const statusStyles = {
  PENDING: { bg: 'var(--surface-sand-deep)', text: 'var(--ink-2)', border: 'var(--border-2)' },
  PROCESSING: { bg: 'var(--accent-tint)', text: 'var(--severity-major-text)', border: 'var(--accent-tint)' },
  REVIEW: { bg: 'var(--spectrum-indigo-tint)', text: 'var(--ink-1)', border: 'var(--spectrum-indigo-tint)' },
  DELIVERED: { bg: 'var(--success-bg)', text: 'var(--success)', border: 'var(--success-bg)' },
  FAILED: { bg: 'var(--danger)', text: 'var(--on-accent)', border: 'var(--danger)' },
};
const statusIcons = { PENDING: Clock, PROCESSING: BarChart3, REVIEW: Scan, DELIVERED: CheckCircle, FAILED: AlertCircle };

export default function DashboardPage() {
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudits = async () => {
      try {
        const resp = await axios.get(`${API}/audits`, { withCredentials: true });
        setAudits(resp.data);
      } catch (e) { console.error('Failed to fetch audits', e); }
      finally { setLoading(false); }
    };
    fetchAudits();
  }, []);

  const completedAudits = audits.filter(a => a.overall_score);
  const avgScore = completedAudits.length ? Math.round(completedAudits.reduce((sum, a) => sum + a.overall_score, 0) / completedAudits.length) : '-';

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Dashboard"
        description="Manage your UX audits, track scores, and view reports."
        canonical="/dashboard"
        noIndex={true}
      />
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
          <div>
            <h1
              style={{
                fontSize: 'var(--type-h2-size)',
                fontWeight: 'var(--type-h2-weight)',
                letterSpacing: 'var(--type-h2-tracking)',
                lineHeight: 'var(--type-h2-leading)',
                color: 'var(--ink-1)'
              }}
              data-testid="dashboard-title"
            >
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            </h1>
            <p className="uxa-helper mt-1">
              Manage your UX audits and validations
            </p>
          </div>
          <Link to="/audit/new" data-testid="new-audit-btn" className="uxa-submit uxa-submit--auto">
            New Audit
            <span className="uxa-submit-knob" aria-hidden="true"><Plus className="w-4 h-4" /></span>
          </Link>
        </div>

        {/* Compare Banner */}
        {audits.filter(a => a.status === 'DELIVERED').length >= 2 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Link 
              to="/audits/compare" 
              data-testid="compare-audits-btn"
              className="flex items-center gap-4 mb-10 p-5 transition-all group"
              style={{ backgroundColor: 'var(--surface-sand)', border: '1px solid var(--border-2)', borderRadius: '18px', minHeight: 'var(--target-min)' }}
            >
              <div className="w-11 h-11 flex items-center justify-center flex-none" style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-2)', borderRadius: '11px' }}>
                <GitCompareArrows className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
              </div>
              <div className="flex-1">
                <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink-1)' }}>Compare Audits</p>
                <p className="uxa-helper">Track UX improvements side-by-side across audit runs</p>
              </div>
              <span style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>View &rarr;</span>
            </Link>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Total Audits', value: audits.length, icon: Scan },
            { label: 'Completed', value: completedAudits.length, icon: CheckCircle },
            { label: 'Avg Score', value: avgScore, icon: TrendingUp },
            { label: 'In Progress', value: audits.filter(a => ['PENDING', 'PROCESSING'].includes(a.status)).length, icon: Clock },
          ].map((stat, i) => (
            <motion.div 
              key={stat.label} 
              initial={{ opacity: 0, y: 15 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: i * 0.08 }}
              className="p-5"
              style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-1)', borderRadius: '18px' }}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3 gap-2">
                <span className="uxa-eyebrow" style={{ fontSize: 'var(--type-dense-size)' }}>{stat.label}</span>
                <stat.icon className="w-4 h-4 flex-none" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
              </div>
              <p style={{ fontFamily: 'var(--font-numeral)', fontSize: '40px', lineHeight: 0.9, letterSpacing: '-0.5px', color: 'var(--ink-1)' }}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Audits List */}
        <h2 className="mb-5" style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.3px', color: 'var(--ink-1)' }}>
          Recent Audits
        </h2>

        {loading ? (
          <div className="flex justify-center py-20">
            <span className="uxa-run-spinner" role="status" aria-label="Loading your audits" />
          </div>
        ) : audits.length === 0 ? (
          /* First-run dashboard — the 03 / EMPTY artboard. With no audits, the
             dashboard becomes the run form: one primary action, and the six
             dimensions shown as what the audit is about to produce.

             THE DESIGN'S SIXTH DIMENSION IS WRONG. It lists "Onboarding", which
             was removed as assessable on 2026-07-15 because a flow cannot be
             audited from a single page. Mobile UX replaced it. The six below
             are the production six, in canonical order, and
             backend/tests/test_six_dimensions.py fails the build if that drifts.

             The design's agency strip ("Agencies run their first three audits
             on one workspace") is not reproduced: there is no workspace, team
             or organisation concept in the backend, and the quota is one free
             audit per account. */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="uxa-app-card"
            data-testid="empty-state"
          >
            <div className="uxa-empty">
              <div style={{ maxWidth: '720px' }}>
                <h3 className="uxa-empty-title">No audits yet</h3>
                <p style={{ fontSize: 'var(--type-lead-size)', lineHeight: 'var(--type-lead-leading)', color: 'var(--ink-2)', margin: '14px 0 0', maxWidth: '560px' }}>
                  Start your first AI-powered UX audit. Upload screenshots and get scored insights in minutes.
                </p>
                <div className="flex flex-wrap items-center" style={{ gap: '16px', marginTop: '28px' }}>
                  <Link to="/audit/new" data-testid="empty-state-cta" className="uxa-cta-accent">
                    Start Your First Audit
                    <span className="uxa-cta-knob" aria-hidden="true"><Plus className="w-4 h-4" /></span>
                  </Link>
                  <span className="uxa-helper">No credit card &middot; one free audit every month</span>
                </div>
              </div>

              <div className="uxa-dim-cards">
                {[
                  ['Navigation', 'Findability and IA'],
                  ['Visual Hierarchy', 'Where the eye lands'],
                  ['Friction Points', 'Forms and dead ends'],
                  ['Accessibility', 'Contrast and targets'],
                  ['Conversion Design', 'CTAs and trust signals'],
                  ['Mobile UX', 'Layout at 390 wide'],
                ].map(([name, note]) => (
                  <div key={name} className="uxa-dim-card">
                    <div className="flex justify-between items-baseline gap-2">
                      <span style={{ fontSize: 'var(--type-body-size)', fontWeight: 700, color: 'var(--ink-1)' }}>{name}</span>
                      <span className="uxa-meta" style={{ color: 'var(--ink-4)' }} aria-hidden="true">&mdash;</span>
                    </div>
                    <div className="uxa-dim-card-track" />
                    <div className="uxa-dim-card-note">{note}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {audits.map((audit, i) => {
              const StatusIcon = statusIcons[audit.status] || Clock;
              const statusStyle = statusStyles[audit.status] || statusStyles.PENDING;
              const isCompleted = audit.status === 'DELIVERED';
              return (
                <motion.div 
                  key={audit.audit_id} 
                  initial={{ opacity: 0, y: 8 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: i * 0.04 }}
                >
                  <div
                    className="p-5 flex items-center gap-4 flex-wrap transition-all"
                    style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-1)', borderRadius: '18px' }}
                    data-testid={`audit-item-${audit.audit_id}`}
                  >
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0 font-bold text-sm"
                      style={{ backgroundColor: 'var(--accent)', color: 'var(--on-accent)', borderRadius: '11px' }}
                      aria-hidden="true"
                    >
                      {(audit.app_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink-1)' }} className="truncate">{audit.app_name || 'Untitled Audit'}</p>
                      <p className="uxa-meta">{audit.industry || 'General'} &middot; {new Date(audit.created_at).toLocaleDateString()}</p>
                    </div>
                    {audit.overall_score != null && (
                      <div className="text-center px-3">
                        <p style={{ fontFamily: 'var(--font-numeral)', fontSize: '28px', lineHeight: 0.9, color: 'var(--ink-1)' }}>{audit.overall_score}</p>
                        <p className="uxa-meta">Score</p>
                      </div>
                    )}
                    <span
                      className="flex items-center gap-1.5"
                      style={{
                        padding: '6px 14px',
                        borderRadius: 'var(--radius-pill)',
                        fontSize: '13px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.text,
                        border: `1px solid ${statusStyle.border}`,
                      }}
                    >
                      <StatusIcon className="w-3 h-3" aria-hidden="true" /> {audit.status}
                    </span>
                    {isCompleted ? (
                      <Link
                        to={`/audit/${audit.audit_id}/report`}
                        className="uxa-submit uxa-submit--sm uxa-submit--auto"
                        data-testid={`view-report-${audit.audit_id}`}
                      >
                        View Report
                        <span className="uxa-submit-knob" aria-hidden="true"><Eye className="w-4 h-4" /></span>
                      </Link>
                    ) : (
                      <Link
                        to={`/audit/${audit.audit_id}/report`}
                        className="uxa-button-secondary"
                        data-testid={`view-status-${audit.audit_id}`}
                      >
                        <Clock className="w-4 h-4" aria-hidden="true" />
                        View Status
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
